// BancosView — lista todos os bancos, com filtros, busca, edição completa,
// upload de JSON (criar ou substituir payload), archive/restore, tags,
// e delete destrutivo (com confirmação).
//
// Paridade com manager legacy:
//   - tags ✅
//   - active/archived (archivedAt) ✅
//   - version (autoincremento no upload) ✅
//   - upload de JSON (file ou paste) ✅
//   - normaliza opcoes→alternativas no client antes de enviar ✅
//   - cleanup automático no delete ✅ (vem do backend)
//
// Não inclui ainda (próxima passada):
//   - Ver usuários com entitlement num banco
//   - Cleanup de órfãs (dry-run)
//   - Download/export do JSON

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  Layers,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Code2,
  Database,
  Eye,
  FileText,
  Globe,
  ListChecks,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Save,
  Search,
  Settings2,
  Sparkles,
  Tag,
  Trash2,
  Upload,
  X,
  XCircle
} from "lucide-react";
import { AnimatedTabs, type AnimatedTabItem } from "@app/ui/components/animated-tabs";
import { Skeleton } from "@app/ui/components/skeleton";
import { QuestionPreview, type PreviewQuestion } from "@app/ui/question-preview";
import { api, ApiError, call, type BankListItem, type GroupListRow, type StudyGroupListRow, type UserRow } from "./api";
import { DissertativeReviewModal } from "./DissertativeReviewModal";
import { BankGrantsForBank } from "./BankGrantsSection";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { VisualBankEditor } from "@app/ui/visual-bank-editor";
import { QuestionJsonByOnePane } from "./QuestionJsonEditor";

const banks = signal<BankListItem[] | null>(null);
const allUsers = signal<UserRow[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const filterCompany = signal<string>("all");
const filterStatus = signal<"all" | "active" | "archived">("active");
const deleting = signal<BankListItem | null>(null);
// Upload e edição viraram tela cheia — feedback 2026-05-20:
// "telas que temos mais de uma ação para ser decidida pelo usuário dentro do
// portal" devem ser views, não modais. [[feedback-views-instead-of-modals]]
const uploadPage = signal<BankListItem | "new" | null>(null);
const editPage = signal<BankListItem | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const [bres, ures] = await Promise.all([api.listBanks(), api.listUsers()]);
    banks.value = bres.banks;
    allUsers.value = ures.users;
    phase.value = "ready";
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(err);
    phase.value = "error";
  }
}

export function BancosView() {
  useEffect(() => {
    load();
  }, []);
  // Quando o admin está criando/substituindo/editando um banco, a tela
  // inteira é dedicada ao formulário (sem modal). A lista volta quando ele
  // dá Voltar ou termina o fluxo com sucesso.
  if (uploadPage.value) {
    return (
      <UploadBankPage
        target={uploadPage.value}
        onBack={() => (uploadPage.value = null)}
      />
    );
  }
  if (editPage.value) {
    return (
      <EditBankPage
        bank={editPage.value}
        onBack={() => (editPage.value = null)}
      />
    );
  }
  if (phase.value === "loading") return <BancosLoading />;
  if (phase.value === "error") return <BancosError />;
  return (
    <>
      <BancosReady />
      {deleting.value && <DeleteBankModal bank={deleting.value} />}
    </>
  );
}

function BancosLoading() {
  return (
    <div class="space-y-3">
      <Skeleton className="h-12 rounded-md" />
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-14 rounded-md" />
      ))}
    </div>
  );
}

function BancosError() {
  return (
    <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
      <div class="flex items-start gap-2">
        <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
        <div>
          <h3 class="text-sm font-bold">Falha ao listar bancos</h3>
          {errMsg.value && (
            <p class="text-xs text-muted-foreground">{errMsg.value}</p>
          )}
          <button
            type="button"
            onClick={() => load()}
            class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    </div>
  );
}

function BancosReady() {
  const rows = banks.value ?? [];
  const q = search.value.toLowerCase().trim();
  const fc = filterCompany.value;
  const fs = filterStatus.value;
  const visible = useMemo(() => {
    return rows.filter((b) => {
      if (fc !== "all" && (b.companyId ?? "_null") !== fc) return false;
      if (fs === "active" && b.archivedAt) return false;
      if (fs === "archived" && !b.archivedAt) return false;
      if (q) {
        const hay =
          `${b.name} ${b.description ?? ""} ${b.companyName ?? ""} ${b.ownerEmail ?? ""} ${b.tags.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q, fc, fs]);

  const companies = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of rows) {
      const id = b.companyId ?? "_null";
      const name = b.companyName ?? "Sem company";
      if (!map.has(id)) map.set(id, name);
    }
    return Array.from(map.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], "pt-BR")
    );
  }, [rows]);

  const counts = useMemo(() => {
    let active = 0,
      archived = 0;
    for (const b of rows) {
      if (b.archivedAt) archived++;
      else active++;
    }
    return { active, archived, total: rows.length };
  }, [rows]);

  const totalQ = visible.reduce((acc, b) => acc + b.questionCount, 0);

  return (
    <div class="space-y-4">
      {/* Toolbar */}
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            placeholder="Buscar nome, descrição, dono, tag…"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <StatusPill
          label="Ativos"
          count={counts.active}
          active={fs === "active"}
          onClick={() => (filterStatus.value = "active")}
        />
        <StatusPill
          label="Arquivados"
          count={counts.archived}
          active={fs === "archived"}
          onClick={() => (filterStatus.value = "archived")}
        />
        <StatusPill
          label="Todos"
          count={counts.total}
          active={fs === "all"}
          onClick={() => (filterStatus.value = "all")}
        />
        <select
          value={filterCompany.value}
          onChange={(e) =>
            (filterCompany.value = (e.target as HTMLSelectElement).value)
          }
          title="Filtrar por company"
          aria-label="Filtrar por company"
          class="rounded-md border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
        >
          <option value="all">Todas companies</option>
          {companies.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => (uploadPage.value = "new")}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Upload width="1.1em" height="1.1em" />
          Novo banco
        </button>
      </div>

      <p class="px-1 text-xs text-muted-foreground">
        {visible.length} de {rows.length} · {totalQ} questões totais visíveis
      </p>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhum banco encontrado nesse filtro.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Banco</th>
                <th class="px-3 py-2">Company</th>
                <th class="px-3 py-2">Tags</th>
                <th class="px-3 py-2 text-right tabular-nums">Questões</th>
                <th class="px-3 py-2 text-right tabular-nums">v</th>
                <th class="px-3 py-2">Status</th>
                <th class="px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((b) => (
                <BankRow key={b.id} bank={b} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({
  label,
  count,
  active,
  onClick
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
        active
          ? "border-primary/50 bg-primary/10 text-primary"
          : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
      }`}
    >
      {label}
      <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums opacity-80">
        {count}
      </span>
    </button>
  );
}

function BankRow({ bank }: { bank: BankListItem }) {
  const isArchived = !!bank.archivedAt;
  return (
    <tr
      class={`border-b border-border/60 transition-colors last:border-0 hover:bg-accent/30 ${
        isArchived ? "opacity-60" : ""
      }`}
    >
      <td class="px-3 py-2">
        <div class="flex items-center gap-1.5 font-semibold">
          {isArchived && <Archive width="0.9em" height="0.9em" className="text-amber-600 dark:text-amber-400" />}
          {bank.name}
        </div>
        {bank.description && (
          <div class="line-clamp-1 text-[11px] text-muted-foreground">
            {bank.description}
          </div>
        )}
        {bank.ownerEmail && (
          <div class="font-mono text-[10px] text-muted-foreground/70">
            dono: {bank.ownerEmail}
          </div>
        )}
      </td>
      <td class="px-3 py-2 text-xs">
        {bank.companyName ? (
          <span class="inline-flex items-center gap-1">
            <Building2 width="0.9em" height="0.9em" className="text-muted-foreground" />
            {bank.companyName}
          </span>
        ) : (
          <span class="text-muted-foreground/70 italic">—</span>
        )}
      </td>
      <td class="px-3 py-2">
        {bank.tags.length === 0 ? (
          <span class="text-xs italic text-muted-foreground/70">—</span>
        ) : (
          <div class="flex flex-wrap gap-1">
            {bank.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                class="inline-flex items-center gap-0.5 rounded bg-accent px-1.5 py-0.5 text-[10px] font-mono"
              >
                <Tag width="0.8em" height="0.8em" />
                {t}
              </span>
            ))}
            {bank.tags.length > 3 && (
              <span class="text-[10px] text-muted-foreground">
                +{bank.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </td>
      <td class="px-3 py-2 text-right tabular-nums font-bold">{bank.questionCount}</td>
      <td class="px-3 py-2 text-right tabular-nums text-xs text-muted-foreground">
        v{bank.version}
      </td>
      <td class="px-3 py-2">
        <div class="flex flex-col items-start gap-0.5">
          {bank.isPublicToCompany ? (
            <span class="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
              <Globe width="0.9em" height="0.9em" />
              Pública na org
            </span>
          ) : (
            <span class="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
              <Lock width="0.9em" height="0.9em" />
              Privada na org
            </span>
          )}
          {bank.visibility === "public" && (
            <span class="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
              <Globe width="0.9em" height="0.9em" />
              Biblioteca
            </span>
          )}
          {bank.visibility === "unlisted" && (
            <span class="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">
              Não listado
            </span>
          )}
          {bank.dissertativeCount > 0 && (
            <span
              class="inline-flex items-center gap-1 rounded-md bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300"
              title={`Versão dissertativa gerada por IA: ${bank.dissertativeCount} questão${bank.dissertativeCount !== 1 ? "ões" : ""}`}
            >
              <Sparkles width="0.9em" height="0.9em" />
              IA Disc. ({bank.dissertativeCount})
            </span>
          )}
        </div>
      </td>
      <td class="px-3 py-2">
        <div class="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => (uploadPage.value = bank)}
            title="Editar questões (visual, prévia ou JSON por questão)"
            aria-label="Editar questões"
            class="inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/10"
          >
            <Pencil width="0.95em" height="0.95em" />
            Editar
          </button>
          <button
            type="button"
            onClick={() => (editPage.value = bank)}
            title="Editar metadados (nome, descrição, visibilidade, tags, dono)"
            aria-label="Editar metadados"
            class="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
          >
            <Settings2 width="0.95em" height="0.95em" />
          </button>
          <button
            type="button"
            onClick={() => (deleting.value = bank)}
            title="Excluir banco"
            aria-label="Excluir banco"
            class="rounded-md border border-red-500/40 bg-red-500/5 px-2 py-1 text-xs text-red-600 hover:bg-red-500/10 dark:text-red-400"
          >
            <Trash2 width="0.95em" height="0.95em" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// =====================
// Modal chrome
// =====================

// =====================
// Prévia paginada das questões parseadas — admin pré-visualiza o JSON
// antes de criar/substituir o banco. Mostra enunciado + alternativas com o
// gabarito destacado, ou texto plano pra essay/composite. Navegação 1-a-1
// (← / →) + jump direto. Recolhível.
// =====================
// Pane de pré-visualização das questões — render igualzinho ao que o aluno
// vê (usa `QuestionPreview` compartilhado em `@app/ui`). Vai na coluna direita
// do modal de upload. Sem toggle de open/close: a pane fica sempre visível
// quando há JSON parseado.
function QuestionsPreviewPane({ questoes }: { questoes: any[] }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [questoes]);

  if (!Array.isArray(questoes) || questoes.length === 0) {
    return (
      <div class="grid place-items-center rounded-md border border-dashed border-border bg-background/40 p-8 text-center text-xs text-muted-foreground">
        <div class="space-y-1">
          <Eye className="mx-auto size-5 opacity-40" />
          <p>Cole/escolha um JSON para ver a prévia das questões aqui.</p>
        </div>
      </div>
    );
  }
  const total = questoes.length;
  const safeIdx = Math.min(Math.max(0, idx), total - 1);
  const q = (questoes[safeIdx] ?? {}) as PreviewQuestion;

  function go(delta: number) {
    setIdx((i) => Math.min(Math.max(0, i + delta), total - 1));
  }
  function jumpTo(n: number) {
    if (!Number.isFinite(n)) return;
    setIdx(Math.min(Math.max(0, n - 1), total - 1));
  }

  return (
    <div class="flex flex-col gap-3">
      <header class="flex items-center justify-between gap-2 text-[11px]">
        <button
          type="button"
          onClick={() => go(-1)}
          disabled={safeIdx === 0}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 font-medium hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Questão anterior"
        >
          <ChevronLeft className="size-3" /> Anterior
        </button>
        <div class="inline-flex items-center gap-1">
          <span class="text-muted-foreground">Questão</span>
          <input
            type="number"
            min={1}
            max={total}
            value={safeIdx + 1}
            onInput={(e) =>
              jumpTo(Number((e.target as HTMLInputElement).value))
            }
            aria-label="Ir para a questão"
            class="w-12 rounded-md border border-border bg-card px-1.5 py-0.5 text-center text-[11px] font-bold outline-none focus:border-primary"
          />
          <span class="text-muted-foreground">de {total}</span>
        </div>
        <button
          type="button"
          onClick={() => go(+1)}
          disabled={safeIdx >= total - 1}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 font-medium hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Próxima questão"
        >
          Próxima <ChevronRight className="size-3" />
        </button>
      </header>
      <QuestionPreview q={q} showGabarito />
    </div>
  );
}

function Modal({
  title,
  icon: Icon,
  children,
  footer,
  onClose,
  maxWidth = "max-w-lg"
}: {
  title: string;
  icon: typeof Database;
  children: any;
  /** Linha de ações renderizada FORA da área de scroll (footer fixo). Evita
   *  vazamento do conteúdo passando atrás de botões Cancelar/Salvar. */
  footer?: any;
  onClose: () => void;
  maxWidth?: string;
}) {
  // ESC fecha. Click no backdrop NÃO fecha — feedback 2026-05-19:
  // formulários de edição perdiam dados por click acidental.
  // [[feedback-modal-no-click-outside-close]]
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
    >
      <div
        class={`flex max-h-[92vh] w-full ${maxWidth} flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl`}
      >
        <header class="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Icon width="1.2em" height="1.2em" className="text-primary" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
        {footer && (
          <div class="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function TagsInput({
  value,
  onChange
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  function add() {
    const t = draft.trim().toLowerCase();
    if (!t || value.includes(t)) {
      setDraft("");
      return;
    }
    onChange([...value, t]);
    setDraft("");
  }
  return (
    <div>
      <div class="flex gap-2">
        <input
          type="text"
          value={draft}
          onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="namespace:valor"
          class="flex-1 rounded-md border border-border bg-background px-3 py-1 text-xs outline-none focus:border-primary"
        />
        <button
          type="button"
          onClick={add}
          class="rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
        >
          + Tag
        </button>
      </div>
      {value.length > 0 && (
        <ul class="mt-2 flex flex-wrap gap-1.5">
          {value.map((t) => (
            <li
              key={t}
              class="inline-flex items-center gap-1 rounded-md bg-accent px-2 py-0.5 text-[11px]"
            >
              <Tag width="0.85em" height="0.85em" />
              <span class="font-mono">{t}</span>
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== t))}
                title="Remover tag"
                aria-label="Remover tag"
                class="text-muted-foreground hover:text-destructive"
              >
                <XCircle width="11" height="11" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// =====================
// Edit page (meta — não mexe no payload) — full-screen, não modal
// =====================

function EditBankPage({
  bank,
  onBack
}: {
  bank: BankListItem;
  onBack: () => void;
}) {
  const [name, setName] = useState(bank.name);
  const [description, setDescription] = useState(bank.description ?? "");
  const [isPublic, setIsPublic] = useState(bank.isPublicToCompany);
  const [visibility, setVisibility] = useState<"private" | "unlisted" | "public">(
    (bank.visibility ?? "private") as "private" | "unlisted" | "public"
  );
  const [libraryFolderId, setLibraryFolderId] = useState<string | null>(
    (bank as any).libraryFolderId ?? null
  );
  const [ownerUserId, setOwnerUserId] = useState<string>(bank.ownerUserId ?? "");
  const [tags, setTags] = useState<string[]>(bank.tags);
  const [folders, setFolders] = useState<Array<{ id: string; name: string }>>([]);

  // Lazy-load das pastas da Biblioteca pra dropdown.
  useEffect(() => {
    void api.listLibraryFolders().then((r) =>
      setFolders(r.folders.map((f) => ({ id: f.id, name: f.name })))
    ).catch(() => {/* ignora se admin sem permissão ou sem rota */});
  }, []);
  const archived = !!bank.archivedAt;
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const updates: Record<string, unknown> = {};
      if (name.trim() !== bank.name) updates.name = name.trim();
      if ((description.trim() || null) !== bank.description) {
        updates.description = description.trim() || null;
      }
      if (isPublic !== bank.isPublicToCompany) {
        updates.isPublicToCompany = isPublic;
      }
      if (visibility !== (bank.visibility ?? "private")) {
        updates.visibility = visibility;
      }
      if (libraryFolderId !== ((bank as any).libraryFolderId ?? null)) {
        updates.libraryFolderId = libraryFolderId;
      }
      if (ownerUserId && ownerUserId !== (bank.ownerUserId ?? "")) {
        updates.ownerUserId = ownerUserId;
      }
      if (JSON.stringify(tags) !== JSON.stringify(bank.tags)) {
        updates.tags = tags;
      }
      if (Object.keys(updates).length === 0) {
        onBack();
        return;
      }
      await api.patchBank(bank.id, updates);
      await load();
      onBack();
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error ? `Falha ao salvar: ${err.message}` : "Falha ao salvar."
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleArchive() {
    setSaving(true);
    try {
      await api.patchBank(bank.id, { archived: !archived });
      await load();
      onBack();
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao arquivar" }) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="space-y-4">
      {/* Header da página — mesmo padrão de UploadBankPage. */}
      <header class="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
        <div class="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold hover:bg-accent"
            title="Voltar para lista de bancos"
            aria-label="Voltar"
          >
            <ArrowLeft width="1em" height="1em" />
            Voltar
          </button>
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-lg font-bold">
              <Pencil width="1.2em" height="1.2em" className="text-primary" />
              <span class="truncate">Editar banco — {bank.name}</span>
            </h2>
            <p class="mt-0.5 text-[11px] text-muted-foreground">
              {bank.questionCount} questões · v{bank.version}
              {bank.companyName ? ` · ${bank.companyName}` : ""}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Save width="1em" height="1em" />
            )}
            Salvar alterações
          </button>
        </div>
      </header>

      <div class="grid gap-4 lg:grid-cols-2">
      <FormSection title="Identidade">
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Nome
          </span>
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            maxLength={200}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </label>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Descrição
          </span>
          <textarea
            value={description}
            onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
            maxLength={500}
            rows={3}
            class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </label>
      </FormSection>

      <FormSection title="Visibilidade na organização">
        <div class="grid grid-cols-2 gap-2">
          <VisibilityOption
            icon={Globe}
            label="Pública na org"
            desc="Todos os alunos da organização veem o banco."
            active={isPublic}
            onClick={() => setIsPublic(true)}
          />
          <VisibilityOption
            icon={Lock}
            label="Restrita"
            desc="Só dono e quem recebeu grant explícito."
            active={!isPublic}
            onClick={() => setIsPublic(false)}
          />
        </div>
      </FormSection>

      <FormSection title="Pasta na Biblioteca">
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Pasta (só relevante quando visibility = público)
          </span>
          <select
            value={libraryFolderId ?? ""}
            onChange={(e) =>
              setLibraryFolderId(
                (e.target as HTMLSelectElement).value || null
              )
            }
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="">Sem categoria</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          {folders.length === 0 && (
            <p class="mt-1 text-[10px] text-muted-foreground">
              Nenhuma pasta criada ainda. Vá em <strong>Biblioteca de provas</strong>{" "}
              no menu pra cadastrar.
            </p>
          )}
        </label>
      </FormSection>

      <FormSection title="Biblioteca de provas (plataforma)">
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <VisibilityOption
            icon={Lock}
            label="Privado"
            desc="Não aparece no Descobrir."
            active={visibility === "private"}
            onClick={() => setVisibility("private")}
          />
          <VisibilityOption
            icon={Lock}
            label="Não listado"
            desc="Acessível só via link direto."
            active={visibility === "unlisted"}
            onClick={() => setVisibility("unlisted")}
          />
          <VisibilityOption
            icon={Globe}
            label="Público / Biblioteca"
            desc="Aparece na Biblioteca de Provas pra qualquer aluno."
            active={visibility === "public"}
            onClick={() => setVisibility("public")}
          />
        </div>
      </FormSection>

      <FormSection title="Dono">
        <OwnerPicker
          selectedId={ownerUserId}
          onChange={setOwnerUserId}
          currentEmail={bank.ownerEmail}
        />
      </FormSection>

      <FormSection title="Tags">
        <TagsInput value={tags} onChange={setTags} />
      </FormSection>

      <BankGrantsForBank bank={bank} allUsers={allUsers.value} />

      <DissertativeSection bankId={bank.id} bankName={bank.name} />

      <FormSection title="Status">
        <div class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-3 py-2">
          <div class="flex-1">
            <div class="text-sm font-semibold">
              {archived ? "Arquivado" : "Ativo"}
            </div>
            <div class="text-[11px] text-muted-foreground">
              Arquivar oculta da listagem dos alunos sem destruir o conteúdo.
              Reversível a qualquer momento.
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggleArchive}
            disabled={saving}
            class={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${
              archived
                ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15 dark:text-emerald-300"
                : "border-amber-500/50 bg-amber-500/10 text-amber-700 hover:bg-amber-500/15 dark:text-amber-300"
            }`}
          >
            {archived ? (
              <>
                <ArchiveRestore width="1em" height="1em" />
                Reativar
              </>
            ) : (
              <>
                <Archive width="1em" height="1em" />
                Arquivar
              </>
            )}
          </button>
        </div>
      </FormSection>
      </div>
    </div>
  );
}

// =====================
// Section: Versão dissertativa (admin gera via IA com prompt
// alternativa-para-dissertativa.md). Lazy-fetch do count atual.
// =====================

function DissertativeSection({
  bankId,
  bankName
}: {
  bankId: string;
  bankName: string;
}) {
  const [count, setCount] = useState<number | null>(null);
  const [mcCount, setMcCount] = useState<number>(0);
  /** Quantas das `mcCount` questões já são essay nativas (tipo==='essay'
   *  ou sem opcoes). Se mcEssayCount === mcCount, o banco já é puramente
   *  dissertativo e não faz sentido rodar a conversão MC→Disc. */
  const [mcEssayCount, setMcEssayCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<{ ok: true; count: number } | { ok: false; reason: string } | null>(null);
  const [sample, setSample] = useState<{ enunciado: string; gabarito: { resposta_modelo: string } } | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  async function handleRemoveAll() {
    if (!confirm(`Remover a versão dissertativa inteira (${count} questões)? O banco MC original fica intacto. Você pode regerar depois se quiser.`)) {
      return;
    }
    try {
      await call(
        `/api/manager/banks/dissertative-item?id=${encodeURIComponent(bankId)}&action=remove-all`,
        { method: "DELETE" }
      );
      setCount(0);
      setResult(null);
      setSample(null);
    } catch (e) {
      alert(`Falha: ${friendlyError(e)}`);
    }
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getBank(bankId);
        if (cancelled) return;
        const payload = (res as any) ?? {};
        const mcArr = Array.isArray(payload.questoes) ? payload.questoes : [];
        const dissArr = Array.isArray((payload as any).questoesDissertativas)
          ? (payload as any).questoesDissertativas
          : [];
        setMcCount(mcArr.length);
        // Conta quantas das questões "MC" são na verdade essay (tipo==='essay'
        // ou sem opcoes). Espelha a checagem do backend em
        // manager-bank-generate-dissertative.ts → isEssayLike.
        const essayLike = mcArr.filter((q: any) => {
          if (q?.tipo === "essay") return true;
          if (!Array.isArray(q?.opcoes) || q.opcoes.length === 0) return true;
          return false;
        }).length;
        setMcEssayCount(essayLike);
        setCount(dissArr.length);
      } catch {
        setMcCount(0);
        setMcEssayCount(0);
        setCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bankId]);

  async function handleGenerate() {
    if (mcCount === 0) return;
    const confirmMsg = count && count > 0
      ? `Já tem ${count} questão(ões) dissertativa(s). Regenerar sobrescreve tudo. Confirmar?`
      : `Gerar ${mcCount} questão(ões) dissertativa(s) com IA? Pode levar 30-60s.`;
    if (!confirm(confirmMsg)) return;
    setGenerating(true);
    setResult(null);
    setSample(null);
    try {
      const r = await call<{ ok: boolean; count?: number; reason?: string; sample?: { primeira?: any } }>(
        `/api/manager/banks/generate-dissertative?id=${encodeURIComponent(bankId)}`,
        { method: "POST" }
      );
      if (r.ok && typeof r.count === "number") {
        setCount(r.count);
        setResult({ ok: true, count: r.count });
        if (r.sample?.primeira) setSample(r.sample.primeira);
      } else {
        setResult({ ok: false, reason: r.reason ?? "Falha desconhecida." });
      }
    } catch (e) {
      setResult({ ok: false, reason: friendlyError(e) });
    } finally {
      setGenerating(false);
    }
  }

  return (
    <FormSection title="Versão dissertativa">
      <div class="space-y-2">
        <p class="text-[11px] text-muted-foreground">
          Gera uma versão dissertativa do banco com IA. As questões MC originais
          ficam <strong>intocadas</strong> — a dissertativa vira uma segunda
          camada do mesmo banco. O aluno alterna entre MC e Dissertativa pelo
          toggle no topo da página do banco. Quem já tinha acesso a "{bankName}"
          passa a ver as duas modalidades.
        </p>

        <div class="rounded-md border border-border bg-background/50 px-3 py-2 text-xs">
          {loading ? (
            <span class="text-muted-foreground">Carregando…</span>
          ) : count && count > 0 ? (
            <>
              <span class="font-semibold text-emerald-700 dark:text-emerald-400">
                ✓ {count} questão(ões) dissertativa(s) geradas
              </span>
              <span class="ml-2 text-muted-foreground">
                ({mcCount} MC originais preservadas)
              </span>
            </>
          ) : (
            <>
              <span class="text-muted-foreground">
                Ainda não gerada.
              </span>
              <span class="ml-2 text-muted-foreground">
                ({mcCount} questões MC disponíveis pra converter)
              </span>
            </>
          )}
        </div>

        {/* Aviso: banco já é dissertativo nativo. Conversão MC→Disc não faz
            sentido aqui — o aluno já estuda dissertativa direto. Histórico:
            Karol/Luís 2026-05-24 com "Prova Semio - Dissertativa - Turma A". */}
        {!loading && mcCount > 0 && mcEssayCount === mcCount && (
          <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300">
            ⚠ Este banco já é <strong>dissertativo nativo</strong> ({mcCount}/{mcCount} questões sem alternativas). Gerar versão IA-disc em cima é redundante. Se já tiver uma (vide contador acima), use <strong>"Remover dissertativa"</strong> pra limpar.
          </div>
        )}

        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={
              generating ||
              loading ||
              mcCount === 0 ||
              (mcCount > 0 && mcEssayCount === mcCount)
            }
            title={
              mcCount > 0 && mcEssayCount === mcCount
                ? "Banco já é dissertativo nativo — não há MC pra converter"
                : undefined
            }
            class="inline-flex items-center gap-1.5 rounded-md border border-violet-500/50 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-500/20 disabled:opacity-50 dark:text-violet-300"
          >
            {generating ? (
              <>
                <Loader2 width="1em" height="1em" className="animate-spin" />
                Gerando… (30–60s)
              </>
            ) : (
              <>
                <Pencil width="1em" height="1em" />
                {count && count > 0 ? "Regenerar todas (IA)" : "Gerar versão dissertativa"}
              </>
            )}
          </button>
          {count != null && count > 0 && (
            <>
              <button
                type="button"
                onClick={() => setReviewOpen(true)}
                disabled={generating || loading}
                class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
                title="Revisar/editar/regenerar questões individualmente"
              >
                <Eye width="1em" height="1em" />
                Revisar ({count})
              </button>
              <button
                type="button"
                onClick={handleRemoveAll}
                disabled={generating || loading}
                class="inline-flex items-center gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-50"
                title="Remove a versão dissertativa inteira (preserva o MC original)"
              >
                <Trash2 width="1em" height="1em" />
                Remover dissertativa
              </button>
            </>
          )}
        </div>

        {result && (
          result.ok ? (
            <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[11px] text-emerald-700 dark:text-emerald-300">
              ✓ Gerada com sucesso ({result.count} questões).
              {sample && (
                <details class="mt-1.5">
                  <summary class="cursor-pointer text-[11px] underline">Ver exemplo da 1ª questão</summary>
                  <div class="mt-1.5 space-y-1.5 rounded-md bg-background/60 px-2 py-2">
                    <div>
                      <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Enunciado
                      </div>
                      <div class="text-xs">{sample.enunciado}</div>
                    </div>
                    <div>
                      <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Resposta-modelo
                      </div>
                      <div class="whitespace-pre-wrap text-xs">{sample.gabarito.resposta_modelo}</div>
                    </div>
                  </div>
                </details>
              )}
            </div>
          ) : (
            <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11px] text-destructive">
              ✗ {result.reason}
            </div>
          )
        )}
      </div>
      {reviewOpen && (
        <DissertativeReviewModal
          bankId={bankId}
          bankName={bankName}
          onClose={() => {
            setReviewOpen(false);
            // Recarrega count caso algo tenha sido removido
            void api
              .getBank(bankId)
              .then((res) => {
                const payload = (res as any) ?? {};
                const dissArr = Array.isArray((payload as any).questoesDissertativas)
                  ? (payload as any).questoesDissertativas
                  : [];
                setCount(dissArr.length);
              })
              .catch(() => {/* ignore */});
          }}
        />
      )}
    </FormSection>
  );
}

// =====================
// Upload page (criar OU substituir payload) — full-screen, não modal
// =====================

function UploadBankPage({
  target,
  onBack
}: {
  target: BankListItem | "new";
  onBack: () => void;
}) {
  const isNew = target === "new";
  const existing = isNew ? null : (target as BankListItem);
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [tags, setTags] = useState<string[]>(existing?.tags ?? []);
  const [isPublic, setIsPublic] = useState(existing?.isPublicToCompany ?? false);
  // Picker da Biblioteca (semântica de Discovery + share por link).
  // [[project-admin-bank-upload-visibility-grants]]
  const [libraryVisibility, setLibraryVisibility] = useState<
    "private" | "unlisted" | "public"
  >((existing?.visibility ?? "private") as "private" | "unlisted" | "public");
  // Emails pra criar grants individuais junto. Resetado após submit.
  const [grantEmailsDraft, setGrantEmailsDraft] = useState("");
  // Turmas (groups) onde adicionar o banco automaticamente após salvar.
  // [[project-turmas-in-student-panel]] — fluxo de criar banco + atribuir
  // direto às turmas sem ter que abrir GruposView depois.
  const [addToGroupIds, setAddToGroupIds] = useState<string[]>([]);
  const [availableGroups, setAvailableGroups] = useState<GroupListRow[]>([]);
  // Grupos de Estudo (study_groups, peer-led) — diferente de turma (groups).
  // [[project-study-groups]] Permite anexar o banco direto a um grupo no upload,
  // sem ter que abrir GruposEstudoView depois e adicionar à mão.
  const [addToStudyGroupIds, setAddToStudyGroupIds] = useState<string[]>([]);
  const [availableStudyGroups, setAvailableStudyGroups] = useState<
    StudyGroupListRow[]
  >([]);
  useEffect(() => {
    void api
      .listGroups()
      .then((r) => setAvailableGroups(r.groups))
      .catch(() => { /* admin pode não ter grupos cadastrados — picker some sozinho */ });
    void api
      .listStudyGroups()
      .then((r) => setAvailableStudyGroups(r.groups))
      .catch(() => { /* sem grupos de estudo — picker some sozinho */ });
  }, []);
  const [ownerUserId, setOwnerUserId] = useState<string>(existing?.ownerUserId ?? "");
  const [jsonText, setJsonText] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<unknown>(null);
  // Importar e JUNTAR vários .json: cada arquivo vira um item aqui, na ordem
  // de mesclagem (admin reordena com ↑/↓). O resultado concatenado vai pro
  // jsonText. Duplicadas (mesmo enunciado) removidas. [[project-pdf-to-dissertative-bank-pipeline]]
  const [mergeFiles, setMergeFiles] = useState<
    Array<{ name: string; questoes: any[] }>
  >([]);
  const [submitting, setSubmitting] = useState(false);
  const [grantsResult, setGrantsResult] = useState<{ granted: string[]; notFound: string[] } | null>(null);
  // Quando editando banco existente, baixa o payload atual da API e abre
  // direto no editor visual — admin não precisa colar JSON do zero pra
  // ajustar uma questão.
  const [loadingPayload, setLoadingPayload] = useState(!isNew);
  // Modo de edição do conteúdo: "json" (textarea cru do banco inteiro),
  // "visual" (formulários por questão + paste de imagem + equações) ou
  // "question-json" (CodeMirror VS Code-style por questão individual,
  // útil pra corrigir uma questão pontual ou colar JSON gerado por IA externa).
  // [[project-visual-bank-editor]]
  // Pra banco existente abre direto no visual; pra novo banco começa em JSON
  // (admin ainda precisa colar/escolher arquivo).
  const [editorMode, setEditorMode] = useState<
    "json" | "visual" | "question-json"
  >(isNew ? "json" : "visual");
  // Pasta da Biblioteca pré-selecionada já no upload. Default null = "sem
  // pasta". Pré-carrega da existing quando for edição. Lista de pastas é
  // lazy-fetched abaixo. [[project-biblioteca-de-provas]]
  const [libraryFolderId, setLibraryFolderId] = useState<string | null>(
    (existing as any)?.libraryFolderId ?? null
  );
  const [libraryFolders, setLibraryFolders] = useState<
    Array<{ id: string; name: string }>
  >([]);
  useEffect(() => {
    void api
      .listLibraryFolders()
      .then((r) =>
        setLibraryFolders(r.folders.map((f) => ({ id: f.id, name: f.name })))
      )
      .catch(() => {/* admin sem permissão / sem pastas — picker some sozinho */});
  }, []);

  useEffect(() => {
    if (isNew || !existing) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getBank(existing.id);
        if (cancelled) return;
        const wrapped = { questoes: res.questoes ?? [] };
        setParsed(wrapped);
        setJsonText(JSON.stringify(wrapped, null, 2));
        setParseError(null);
      } catch (err) {
        if (cancelled) return;
        setParseError(friendlyError(err, { fallback: "Falha ao carregar banco" }));
      } finally {
        if (!cancelled) setLoadingPayload(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, existing]);
  // Índice da questão atual no modo "question-json" (também usado pra
  // sincronizar a prévia ao lado). Compartilhado entre toggles.
  const [questionIdx, setQuestionIdx] = useState(0);

  // Atualiza o estado `parsed` E re-serializa para o textarea JSON quando o
  // editor visual altera questões. Mantém ambos modos sincronizados.
  function applyQuestoesFromVisual(next: PreviewQuestion[]) {
    const wrapped = { questoes: next };
    setParsed(wrapped);
    setJsonText(JSON.stringify(wrapped, null, 2));
    setParseError(null);
  }

  function tryParse(raw: string) {
    setJsonText(raw);
    setParseError(null);
    setParsed(null);
    if (!raw.trim()) return;
    try {
      const obj = JSON.parse(raw);
      // Aceita array puro ou { questoes: [...] }
      let questoes: any[] | null = null;
      if (Array.isArray(obj)) questoes = obj;
      else if (obj && typeof obj === "object" && Array.isArray(obj.questoes))
        questoes = obj.questoes;
      else if (obj && typeof obj === "object" && Array.isArray(obj.questions))
        questoes = obj.questions;
      if (!questoes) {
        setParseError("JSON precisa ser array de questões ou { questoes: [...] }.");
        return;
      }
      setParsed({ questoes });
    } catch (err) {
      setParseError(
        err instanceof Error ? `JSON inválido: ${err.message}` : "JSON inválido"
      );
    }
  }

  async function handleFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = ""; // permite re-escolher os mesmos arquivos depois
    if (files.length === 0) return;
    await addFiles(files);
  }

  // Drag-and-drop de arquivo .json em cima da área do editor JSON. Aceita o
  // primeiro arquivo com type JSON ou extensão `.json`. Outros tipos viram
  // erro amigável em vez de silenciar.
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);

  function isJsonFile(f: File): boolean {
    if (f.type === "application/json" || f.type === "text/json") return true;
    return /\.json$/i.test(f.name);
  }

  function handleDragEnter(e: DragEvent) {
    if (!e.dataTransfer) return;
    // Só ativa se realmente há arquivo no drag (não texto, link, etc.).
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOver(true);
  }

  function handleDragOver(e: DragEvent) {
    if (!e.dataTransfer) return;
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }

  function handleDragLeave(e: DragEvent) {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragOver(false);
  }

  async function handleDrop(e: DragEvent) {
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    const dropped = e.dataTransfer?.files
      ? Array.from(e.dataTransfer.files)
      : [];
    if (dropped.length === 0) return;
    const jsons = dropped.filter(isJsonFile);
    if (jsons.length === 0) {
      setParseError("Só aceito arquivos .json aqui.");
      return;
    }
    await addFiles(jsons);
  }

  // ===== Importar e juntar vários .json (ordem controlável) ============
  function questoesFromParsed(obj: unknown): any[] | null {
    if (Array.isArray(obj)) return obj;
    if (obj && typeof obj === "object") {
      const o = obj as Record<string, unknown>;
      if (Array.isArray(o.questoes)) return o.questoes as any[];
      if (Array.isArray(o.questions)) return o.questions as any[];
    }
    return null;
  }

  // Concatena os arquivos NA ORDEM da lista, carimba arquivo_origem/fonte pelo
  // nome do arquivo (quando a questão não traz origem) e remove duplicadas por
  // enunciado normalizado (caso+pergunta). Mantém a 1ª ocorrência.
  function mergeStaged(files: Array<{ name: string; questoes: any[] }>): any[] {
    const out: any[] = [];
    const seen = new Set<string>();
    for (const f of files) {
      const fonte = f.name.replace(/\.json$/i, "");
      for (const raw of Array.isArray(f.questoes) ? f.questoes : []) {
        if (!raw || typeof raw !== "object") continue;
        const q = raw as Record<string, any>;
        const caso = typeof q.caso === "string" ? q.caso : "";
        const perg = typeof q.pergunta === "string" ? q.pergunta : "";
        const key = `${caso}\n${perg}`.toLowerCase().replace(/\s+/g, " ").trim();
        if (key) {
          if (seen.has(key)) continue;
          seen.add(key);
        }
        const hasFonte = typeof q.fonte === "string" && q.fonte.trim();
        const hasArquivo =
          typeof q.arquivo_origem === "string" && q.arquivo_origem.trim();
        out.push({
          ...q,
          ...(hasFonte ? {} : { fonte }),
          ...(hasArquivo ? {} : { arquivo_origem: fonte })
        });
      }
    }
    return out;
  }

  function applyStaged(files: Array<{ name: string; questoes: any[] }>) {
    if (files.length === 0) {
      setJsonText("");
      setParsed(null);
      setParseError(null);
      return;
    }
    const merged = mergeStaged(files);
    const wrapped = { questoes: merged };
    setParsed(wrapped);
    setJsonText(JSON.stringify(wrapped, null, 2));
    setParseError(null);
  }

  async function addFiles(files: File[]) {
    const jsons = files.filter(isJsonFile);
    if (jsons.length === 0) {
      setParseError("Selecione arquivos .json.");
      return;
    }
    try {
      const loaded = await Promise.all(
        jsons.map(async (file) => {
          const text = await file.text();
          const obj = JSON.parse(text);
          const q = questoesFromParsed(obj);
          if (!q) {
            throw new Error(
              `"${file.name}": precisa ser um array ou { questoes: [...] }.`
            );
          }
          return { name: file.name, questoes: q };
        })
      );
      setMergeFiles((prev) => {
        const next = [...prev, ...loaded];
        applyStaged(next);
        return next;
      });
    } catch (err) {
      setParseError(
        err instanceof Error ? err.message : "Falha ao ler arquivo JSON."
      );
    }
  }

  function moveStaged(idx: number, dir: -1 | 1) {
    setMergeFiles((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      applyStaged(next);
      return next;
    });
  }

  function removeStaged(idx: number) {
    setMergeFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      applyStaged(next);
      return next;
    });
  }

  function clearStaged() {
    // Solta só a lista — não mexe no textarea (admin pode ter editado à mão).
    setMergeFiles([]);
  }

  const parsedCount =
    parsed && typeof parsed === "object" && parsed !== null
      ? ((parsed as Record<string, unknown>).questoes as any[]).length
      : 0;

  const valid = name.trim().length > 0 && parsed !== null && !parseError;

  // Quando editando banco já existente, abre modal de confirmação antes do
  // upload pra admin escolher se versiona (e notifica) ou salva silencioso
  // (ajuste cosmético). [[project-question-wysiwyg-editor]]
  const [saveConfirm, setSaveConfirm] = useState<null | "ask">(null);

  // Tabs do editor: "Configurações" (meta — name/visibility/tags/grants/owner)
  // e "Questões" (conteúdo pesado — JSON/Editor visual/JSON por questão).
  // Default depende do contexto:
  //   - NEW: começa em "Questões" porque admin precisa colar JSON pra criar
  //   - EDIT: começa em "Configurações" porque maioria dos edits é meta-only
  //     e evita o custo de renderizar o editor visual de cara.
  // [[project-question-wysiwyg-editor]]
  const [editorTab, setEditorTab] = useState<"config" | "questoes" | "dissertativa">(
    isNew ? "questoes" : "config"
  );
  // Lazy render do tab de questões: só renderiza pesado depois que admin
  // entrou pela 1ª vez. Quando ele sai, mantemos montado pra preservar
  // estado (scroll, índice da questão atual etc).
  const [questoesEverOpened, setQuestoesEverOpened] = useState(
    editorTab === "questoes"
  );
  useEffect(() => {
    if (editorTab === "questoes" && !questoesEverOpened) {
      setQuestoesEverOpened(true);
    }
  }, [editorTab, questoesEverOpened]);

  function handleSubmit() {
    if (!valid) return;
    // Banco novo: salva direto, sem ask
    if (!existing?.id) {
      void doSave({ silent: false });
      return;
    }
    setSaveConfirm("ask");
  }

  async function doSave(opts: { silent: boolean }) {
    setSaveConfirm(null);
    setSubmitting(true);
    try {
      // Parse das emails do textarea (separados por vírgula, espaço ou linha).
      const grantEmails = grantEmailsDraft
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api.uploadBank({
        id: existing?.id,
        name: name.trim(),
        description: description.trim() || null,
        data: parsed,
        tags,
        isPublicToCompany: isPublic,
        ownerUserId: ownerUserId || undefined,
        visibility: libraryVisibility,
        // Só faz sentido pré-categorizar na Biblioteca quando o banco vai
        // pro catálogo público. Pra unlisted/private mandamos null pra não
        // sujar a estrutura de pastas com bancos invisíveis.
        libraryFolderId:
          libraryVisibility === "public" ? libraryFolderId : null,
        grantEmails: grantEmails.length > 0 ? grantEmails : undefined,
        silent: opts.silent
      });

      // Adiciona o banco recém-criado/atualizado às turmas selecionadas.
      // Fetch + patch por grupo: simples e seguro contra race com bankIds[]
      // alterado em outra aba. Auto-sync rola no backend.
      const bankIdSaved = res?.bank?.id;
      if (bankIdSaved && addToGroupIds.length > 0) {
        await Promise.all(
          addToGroupIds.map(async (gid) => {
            try {
              const detail = await api.getGroup(gid);
              const existingIds = detail.group.bankIds ?? [];
              if (!existingIds.includes(bankIdSaved)) {
                await api.patchGroup(gid, {
                  bankIds: [...existingIds, bankIdSaved]
                });
              }
            } catch (err) {
              console.warn(
                "[bancos-upload] falha ao anexar banco à turma",
                gid,
                err instanceof Error ? err.message : String(err)
              );
            }
          })
        );
      }

      // Anexa o banco aos Grupos de Estudo selecionados. add-bank já dedupe
      // (409 quando o banco já está no repositório do grupo) e materializa
      // grants pros membros atuais — então 409 é "já estava lá", ignoramos.
      if (bankIdSaved && addToStudyGroupIds.length > 0) {
        await Promise.all(
          addToStudyGroupIds.map(async (gid) => {
            try {
              await api.studyGroupAddBank(gid, bankIdSaved);
            } catch (err) {
              const dup =
                err instanceof ApiError && err.status === 409;
              if (!dup) {
                console.warn(
                  "[bancos-upload] falha ao anexar banco ao grupo de estudo",
                  gid,
                  err instanceof Error ? err.message : String(err)
                );
              }
            }
          })
        );
      }
      await load();
      // Se houve grants e algum email não foi encontrado, mostra resumo
      // antes de fechar — clareza pro admin saber o que pegou e o que não.
      // [[feedback-user-clarity-context]]
      if (
        grantEmails.length > 0 &&
        res &&
        (res as any).grants &&
        ((res as any).grants.notFound?.length > 0 ||
          (res as any).grants.granted?.length > 0)
      ) {
        setGrantsResult((res as any).grants);
        // Não volta pra lista enquanto o admin não der OK no resumo.
      } else {
        onBack();
      }
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error ? `Falha ao salvar: ${err.message}` : "Falha ao salvar."
      });
    } finally {
      setSubmitting(false);
    }
  }

  const PageIcon = isNew ? Upload : Pencil;

  return (
    <div class="space-y-4">
      {/* Header da página — substitui a chrome do modal. */}
      <header class="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
        <div class="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold hover:bg-accent"
            title="Voltar para lista de bancos"
            aria-label="Voltar"
          >
            <ArrowLeft width="1em" height="1em" />
            Voltar
          </button>
          <div class="min-w-0">
            <h2 class="flex items-center gap-2 text-lg font-bold">
              <PageIcon width="1.2em" height="1.2em" className="text-primary" />
              <span class="truncate">
                {isNew ? "Novo banco" : `Editar conteúdo — ${existing!.name}`}
              </span>
            </h2>
            {!isNew && existing && (
              <p class="mt-0.5 text-[11px] text-muted-foreground">
                Versão atual: v{existing.version} · {existing.questionCount} questões. Após salvar — se as questões mudarem — passa para v{existing.version + 1}.
              </p>
            )}
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!valid || submitting}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : isNew ? (
              <Plus width="1em" height="1em" />
            ) : (
              <Save width="1em" height="1em" />
            )}
            {isNew ? "Criar banco" : "Salvar alterações"}
          </button>
        </div>
      </header>

      {loadingPayload && (
        <div class="flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Carregando questões do banco…
        </div>
      )}

      {/* Tabs do editor. Pra novos bancos, "Questões" abre default; pra edição
          de banco existente, "Configurações" abre default — separa o trabalho
          de meta (rápido) do de conteúdo (pesado). [[project-question-wysiwyg-editor]] */}
      <AnimatedTabs<"config" | "questoes" | "dissertativa">
        ariaLabel="Seções do editor de banco"
        items={[
          {
            id: "config",
            label: "Configurações do banco",
            shortLabel: "Configurações",
            icon: Settings2
          },
          {
            id: "questoes",
            label: isNew ? "Conteúdo do banco" : "Questões",
            shortLabel: isNew ? "Conteúdo" : "Questões",
            icon: ListChecks
          },
          // Terceira tab dedicada à versão dissertativa gerada por IA.
          // Só aparece em modo edição (precisa do bank.id) — em "novo banco"
          // ainda não há onde gerar/editar IA-disc. [[project-question-wysiwyg-editor]]
          ...(!isNew && existing?.id
            ? [{
                id: "dissertativa" as const,
                label: "Versão dissertativa (IA)",
                shortLabel: "Dissertativa IA",
                icon: Sparkles
              }]
            : [])
        ] as AnimatedTabItem<"config" | "questoes" | "dissertativa">[]}
        value={editorTab}
        onChange={setEditorTab}
      />

      {/* Layout 2-col só em modo JSON dentro da tab Questões (form + prévia
          sticky lateral). Nos modos "visual" e "question-json" o próprio
          editor já traz sua prévia, então fica em coluna única.
          Na tab Configurações o layout é sempre single-col. */}
      <div
        class={
          editorTab === "questoes" && editorMode === "json"
            ? "grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]"
            : "space-y-3"
        }
      >
        <div class="space-y-3">
          {editorTab === "config" && (
          <FormSection title="Identidade">
            <label class="block">
              <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Nome
              </span>
              <input
                type="text"
                value={name}
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
                maxLength={200}
                placeholder="Ex: ENAMED 2026 - Preliminar - Caderno 1"
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
            </label>
            <label class="block">
              <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Descrição (opcional)
              </span>
              <textarea
                value={description}
                onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
                maxLength={500}
                rows={2}
                class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
            </label>
          </FormSection>
          )}

          {editorTab === "questoes" && questoesEverOpened && (
          <FormSection title="Conteúdo do banco">
            <div class="space-y-2">
              {/* Toggle de modo: JSON cru vs editor visual. Ambos refletem
                  a MESMA fonte de verdade — alternar não perde alterações. */}
              <div class="inline-flex items-center gap-0 rounded-md border border-border bg-background p-0.5">
                <button
                  type="button"
                  onClick={() => setEditorMode("json")}
                  class={`rounded px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                    editorMode === "json"
                      ? "bg-primary text-white"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  JSON
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode("visual")}
                  disabled={!parsed || !!parseError}
                  title={!parsed ? "Cole/escolha um JSON antes pra abrir o editor visual" : "Editor visual"}
                  class={`rounded px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    editorMode === "visual"
                      ? "bg-primary text-white"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Editor visual
                </button>
                <button
                  type="button"
                  onClick={() => setEditorMode("question-json")}
                  disabled={!parsed || !!parseError}
                  title={
                    !parsed
                      ? "Cole/escolha um JSON antes pra abrir o editor por questão"
                      : "Editor JSON por questão (VS Code-style) — útil pra corrigir uma questão pontual ou colar JSON gerado por IA externa"
                  }
                  class={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    editorMode === "question-json"
                      ? "bg-primary text-white"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Code2 className="size-3" />
                  JSON por questão
                </button>
              </div>

              {editorMode === "json" && (
                <div
                  class={`relative rounded-md border border-dashed transition-colors ${
                    isDragOver
                      ? "border-primary bg-primary/5"
                      : "border-transparent"
                  }`}
                  onDragEnter={handleDragEnter as any}
                  onDragOver={handleDragOver as any}
                  onDragLeave={handleDragLeave as any}
                  onDrop={handleDrop as any}
                >
                  <div class="flex flex-wrap items-center gap-2 p-1">
                    <label class="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent">
                      <Upload width="1em" height="1em" />
                      Escolher .json (1 ou vários)
                      <input
                        type="file"
                        accept=".json,application/json"
                        multiple
                        onChange={handleFile}
                        class="hidden"
                      />
                    </label>
                    <span class="text-[10px] text-muted-foreground">
                      arraste <code class="rounded bg-accent px-1 font-mono">.json</code> aqui
                      (pode soltar vários), escolha os arquivos ou cole abaixo
                    </span>
                  </div>
                  <textarea
                    value={jsonText}
                    onInput={(e) => tryParse((e.target as HTMLTextAreaElement).value)}
                    rows={6}
                    placeholder='[{"pergunta": "...", "alternativas": ["A", "B", "C", "D", "E"], "correta": 1}, ...]'
                    class={`w-full resize-y rounded-md border bg-background px-3 py-1.5 font-mono text-[11px] outline-none focus:border-primary ${
                      parseError ? "border-destructive" : "border-border"
                    }`}
                  />
                  {isDragOver && (
                    // Overlay leve só pra reforçar o "soltar aqui" — não bloqueia
                    // o evento; o handler de drop está no container.
                    <div class="pointer-events-none absolute inset-0 grid place-items-center rounded-md bg-primary/5 text-xs font-bold text-primary">
                      Solte os arquivos .json para carregar
                    </div>
                  )}
                  {mergeFiles.length > 0 && (
                    <div class="mt-2 rounded-md border border-violet-500/40 bg-violet-500/5 p-2">
                      <div class="mb-1.5 flex items-center justify-between">
                        <span class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                          <Layers width="1em" height="1em" />
                          Juntando {mergeFiles.length} arquivos · ordem da mesclagem
                        </span>
                        <button
                          type="button"
                          onClick={clearStaged}
                          class="text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                          Limpar lista
                        </button>
                      </div>
                      <ol class="space-y-1">
                        {mergeFiles.map((f, idx) => (
                          <li
                            key={`${f.name}-${idx}`}
                            class="flex items-center gap-2 rounded border border-border bg-background px-2 py-1 text-[11px]"
                          >
                            <span class="grid size-4 shrink-0 place-items-center rounded bg-primary/10 text-[10px] font-bold text-primary">
                              {idx + 1}
                            </span>
                            <span class="min-w-0 flex-1 truncate font-medium">
                              {f.name}
                            </span>
                            <span class="shrink-0 text-muted-foreground">
                              {f.questoes.length} q
                            </span>
                            <div class="flex shrink-0 items-center">
                              <button
                                type="button"
                                onClick={() => moveStaged(idx, -1)}
                                disabled={idx === 0}
                                class="grid size-5 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30"
                                aria-label="Mover pra cima"
                                title="Mover pra cima"
                              >
                                <ArrowUp width="0.9em" height="0.9em" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveStaged(idx, 1)}
                                disabled={idx === mergeFiles.length - 1}
                                class="grid size-5 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30"
                                aria-label="Mover pra baixo"
                                title="Mover pra baixo"
                              >
                                <ArrowDown width="0.9em" height="0.9em" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeStaged(idx)}
                                class="grid size-5 place-items-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                aria-label="Remover"
                                title="Remover da mesclagem"
                              >
                                <X width="0.9em" height="0.9em" />
                              </button>
                            </div>
                          </li>
                        ))}
                      </ol>
                      <p class="mt-1.5 text-[10px] text-muted-foreground">
                        Duplicadas (mesmo enunciado) são removidas · origem
                        carimbada pelo nome do arquivo. Reordene e o JSON acima
                        atualiza sozinho.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {editorMode === "visual" && parsed && !parseError && (
                <VisualBankEditor
                  questoes={
                    ((parsed as Record<string, unknown>).questoes as PreviewQuestion[]) ?? []
                  }
                  onChange={applyQuestoesFromVisual}
                />
              )}

              {editorMode === "question-json" && parsed && !parseError && (
                <QuestionJsonByOnePane
                  questoes={
                    ((parsed as Record<string, unknown>).questoes as PreviewQuestion[]) ?? []
                  }
                  index={questionIdx}
                  onIndexChange={setQuestionIdx}
                  onChange={applyQuestoesFromVisual}
                />
              )}

              {parseError && (
                <div class="rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-[11px] text-destructive">
                  {parseError}
                </div>
              )}
              {parsed && !parseError && (
                <div class="rounded-md border border-emerald-500/40 bg-emerald-500/5 px-2 py-1 text-[11px] text-emerald-700 dark:text-emerald-300">
                  ✓ {parsedCount}{" "}
                  {parsedCount === 1 ? "questão parseada" : "questões parseadas"}.
                  {existing && (
                    <>
                      {" "}
                      Vai substituir o payload (versão atual: v{existing.version} →
                      v{existing.version + 1}).
                    </>
                  )}
                </div>
              )}
            </div>
          </FormSection>
          )}

          {editorTab === "config" && (
          <>
          <FormSection title="Visibilidade na organização">
            <div class="grid grid-cols-2 gap-2">
              <VisibilityOption
                icon={Globe}
                label="Pública na org"
                desc="Todos os alunos da organização veem o banco."
                active={isPublic}
                onClick={() => setIsPublic(true)}
              />
              <VisibilityOption
                icon={Lock}
                label="Restrita"
                desc="Só dono e quem recebeu grant explícito."
                active={!isPublic}
                onClick={() => setIsPublic(false)}
              />
            </div>
          </FormSection>

          <FormSection title="Biblioteca de Provas (plataforma)">
            <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <VisibilityOption
                icon={Lock}
                label="Private"
                desc="Só dono e quem tiver grant individual."
                active={libraryVisibility === "private"}
                onClick={() => setLibraryVisibility("private")}
              />
              <VisibilityOption
                icon={Globe}
                label="Unlisted"
                desc="Não aparece na Biblioteca, mas qualquer um com link acessa."
                active={libraryVisibility === "unlisted"}
                onClick={() => setLibraryVisibility("unlisted")}
              />
              <VisibilityOption
                icon={Globe}
                label="Public"
                desc="Aparece na Biblioteca de Provas (catálogo público)."
                active={libraryVisibility === "public"}
                onClick={() => setLibraryVisibility("public")}
              />
            </div>
            <p class="mt-2 text-[10px] text-muted-foreground">
              Use <strong>Private</strong> + grants individuais abaixo para
              publicar para um grupo fechado. <strong>Public</strong> joga na
              Biblioteca global (use só para conteúdo curado).
            </p>

            {/* Picker de pasta da Biblioteca — só aparece quando o banco vai
                pro catálogo público. Evita ida extra à tela de edição de meta
                pra categorizar. [[project-biblioteca-de-provas]] */}
            {libraryVisibility === "public" && (
              <label class="mt-3 block border-t border-border pt-3">
                <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Pasta na Biblioteca (opcional)
                </span>
                <select
                  value={libraryFolderId ?? ""}
                  onChange={(e) =>
                    setLibraryFolderId(
                      (e.target as HTMLSelectElement).value || null
                    )
                  }
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                >
                  <option value="">Sem categoria</option>
                  {libraryFolders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                {libraryFolders.length === 0 ? (
                  <p class="mt-1 text-[10px] text-muted-foreground">
                    Nenhuma pasta criada ainda. Crie em{" "}
                    <strong>Biblioteca de provas</strong> no menu lateral para
                    categorizar.
                  </p>
                ) : (
                  <p class="mt-1 text-[10px] text-muted-foreground">
                    O banco aparece dentro desta pasta na Biblioteca pública.
                    Deixe em <em>sem categoria</em> se preferir decidir depois.
                  </p>
                )}
              </label>
            )}
          </FormSection>

          <FormSection title="Conceder acesso individual (opcional)">
            <textarea
              value={grantEmailsDraft}
              onInput={(e) =>
                setGrantEmailsDraft((e.target as HTMLTextAreaElement).value)
              }
              rows={3}
              placeholder={"emails separados por vírgula, espaço ou linha\nex: aluna@email.com, professor@email.com"}
              class="w-full resize-y rounded-md border border-border bg-background px-3 py-1.5 font-mono text-[11px] outline-none focus:border-primary"
            />
            <p class="mt-1 text-[10px] text-muted-foreground">
              Cada email recebe um <strong>grant individual</strong> (pode
              estudar e fazer prova). Emails sem conta cadastrada são
              reportados ao salvar (não falha o upload). Ideal para times
              pequenos sem mexer em visibility.
            </p>
            {grantsResult && (
              <div class="mt-2 space-y-1 rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px]">
                {grantsResult.granted.length > 0 && (
                  <p class="text-emerald-700 dark:text-emerald-300">
                    ✓ Grants criados: <strong>{grantsResult.granted.length}</strong>
                    {" "}({grantsResult.granted.join(", ")})
                  </p>
                )}
                {grantsResult.notFound.length > 0 && (
                  <p class="text-amber-700 dark:text-amber-300">
                    ⚠ Emails sem conta (não criados):{" "}
                    <strong>{grantsResult.notFound.length}</strong>
                    {" "}({grantsResult.notFound.join(", ")})
                  </p>
                )}
              </div>
            )}
          </FormSection>

          {availableGroups.length > 0 && (
            <FormSection title="Adicionar às turmas (opcional)">
              <BankGroupsMultiSelect
                groups={availableGroups}
                selected={addToGroupIds}
                onChange={setAddToGroupIds}
              />
              <p class="mt-1 text-[10px] text-muted-foreground">
                Membros das turmas selecionadas ganham acesso automático ao banco
                (idempotente — não toca grants já existentes).
              </p>
            </FormSection>
          )}

          {availableStudyGroups.length > 0 && (
            <FormSection title="Adicionar a Grupos de Estudo (opcional)">
              <BankStudyGroupsMultiSelect
                groups={availableStudyGroups}
                selected={addToStudyGroupIds}
                onChange={setAddToStudyGroupIds}
              />
              <p class="mt-1 text-[10px] text-muted-foreground">
                O banco entra no repositório do grupo (como um item) e os membros
                atuais ganham acesso. Idempotente — se já estiver no grupo, ignora.
              </p>
            </FormSection>
          )}

          <FormSection title="Dono">
            <OwnerPicker
              selectedId={ownerUserId}
              onChange={setOwnerUserId}
              currentEmail={existing?.ownerEmail}
            />
          </FormSection>

          <FormSection title="Tags">
            <TagsInput value={tags} onChange={setTags} />
          </FormSection>
          </>
          )}

          {/* Tab dedicada à versão dissertativa gerada por IA. Hospeda
              os controles (gerar/regenerar/revisar/remover) e o modal de
              revisão por questão. Só renderiza em modo edição. Pedido
              2026-05-24 ("dá uma checada no reconhecimento + 3a tab"). */}
          {editorTab === "dissertativa" && existing?.id && (
            <DissertativeSection
              bankId={existing.id}
              bankName={existing.name ?? name}
            />
          )}
        </div>

        {/* Coluna direita — prévia das questões. Sticky a partir de lg
            para acompanhar o scroll natural da página. Sem max-h: o usuário
            rola a página inteira em vez de scroll aninhado.
            Suprimida em modo visual (o editor já tem sua própria prévia).
            Só faz sentido na tab Questões. */}
        {editorTab === "questoes" && editorMode === "json" && (
          <aside class="lg:sticky lg:top-4 lg:self-start">
            <div class="rounded-md border border-border bg-card/40 p-3">
              <div class="mb-3 flex items-center gap-1.5 text-xs font-bold">
                <Eye className="size-3.5 text-primary" />
                Pré-visualização das questões
                <span class="ml-auto text-[10px] font-medium text-muted-foreground">
                  como o aluno vê
                </span>
              </div>
              <QuestionsPreviewPane
                questoes={
                  parsed && !parseError
                    ? ((parsed as Record<string, unknown>).questoes as any[]) ?? []
                    : []
                }
              />
            </div>
          </aside>
        )}
      </div>

      {saveConfirm === "ask" && (
        <SaveBankConfirmModal
          bankName={name}
          onCancel={() => setSaveConfirm(null)}
          onVersioned={() => void doSave({ silent: false })}
          onSilent={() => void doSave({ silent: true })}
        />
      )}
    </div>
  );
}

// =====================
// Modal de confirmação ao salvar banco existente
// =====================
// Pergunta ao admin se ele quer (a) versionar+notificar grantees ou
// (b) salvar silencioso (sem bump, sem notif) — útil pra ajuste cosmético.
// [[project-question-wysiwyg-editor]]
function SaveBankConfirmModal({
  bankName,
  onCancel,
  onVersioned,
  onSilent
}: {
  bankName: string;
  onCancel: () => void;
  onVersioned: () => void;
  onSilent: () => void;
}) {
  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div class="w-full max-w-lg overflow-hidden rounded-lg border bg-background shadow-xl">
        <header class="border-b px-5 py-3">
          <h2 class="text-base font-semibold">Como você quer salvar?</h2>
          <p class="mt-1 text-xs text-muted-foreground">
            Você está editando o banco{" "}
            <strong class="text-foreground">{bankName || "(sem nome)"}</strong>.
            Escolha o tipo de save:
          </p>
        </header>

        <div class="space-y-3 p-5">
          {/* Opção 1: versionar */}
          <button
            type="button"
            onClick={onVersioned}
            class="block w-full rounded-lg border-2 border-violet-500/50 bg-violet-500/5 p-4 text-left transition hover:bg-violet-500/10"
          >
            <div class="flex items-center gap-2">
              <Save className="size-5 text-violet-600" />
              <span class="text-sm font-semibold">
                Salvar versionando (recomendado)
              </span>
              <span class="ml-auto rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
                v atual + 1
              </span>
            </div>
            <p class="mt-2 text-xs text-muted-foreground">
              Incrementa a versão do banco. Alunos com grant individual recebem
              notificação <em>"banco atualizado"</em>. Quem já abriu o banco
              antes vê a nova versão silenciosamente na próxima abertura.
              Progresso e respostas salvas são preservados.
            </p>
          </button>

          {/* Opção 2: silencioso */}
          <button
            type="button"
            onClick={onSilent}
            class="block w-full rounded-lg border border-border bg-card p-4 text-left transition hover:bg-muted"
          >
            <div class="flex items-center gap-2">
              <Eye className="size-5 text-muted-foreground" />
              <span class="text-sm font-semibold">Salvar silencioso</span>
              <span class="ml-auto rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                sem bump
              </span>
            </div>
            <p class="mt-2 text-xs text-muted-foreground">
              Atualiza o conteúdo mantendo a mesma versão. <strong>Nenhuma
              notificação é enviada.</strong> Use só pra ajustes cosméticos
              (typo, ortografia, formatação) que não justificam alertar quem
              já fez a prova.
            </p>
          </button>

          {/* Aviso sobre consistência */}
          <div class="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-700 dark:text-amber-300">
            <strong>Sobre consistência:</strong> em ambos os modos, o ID do
            banco é preservado. Progresso e respostas dos alunos referenciam
            o banco pelo ID e pelas questões individuais (UUID) — então
            <em> editar texto, formatação, alternativas ou gabarito não quebra
            registros existentes</em>. Reordenar/remover questões pode
            desalinhar progresso de quem já estava no meio — use com critério.
          </div>
        </div>

        <footer class="flex justify-end gap-2 border-t bg-muted/30 px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            class="rounded-md border bg-background px-4 py-1.5 text-sm font-medium hover:bg-muted"
          >
            Cancelar
          </button>
        </footer>
      </div>
    </div>
  );
}

// =====================
// Delete modal
// =====================

function DeleteBankModal({ bank }: { bank: BankListItem }) {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const ok = confirmText.trim() === bank.name.trim();

  async function handleDelete() {
    if (!ok) return;
    setSubmitting(true);
    try {
      await api.deleteBank(bank.id);
      await load();
      deleting.value = null;
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error ? `Falha ao excluir: ${err.message}` : "Falha ao excluir."
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Excluir banco" icon={AlertCircle} onClose={() => (deleting.value = null)}>
      <div class="space-y-3">
        <p class="text-sm text-muted-foreground">
          Vai apagar <strong class="font-semibold text-foreground">{bank.name}</strong>{" "}
          ({bank.questionCount} questões) e todo o progresso/resultado dos alunos
          neste banco. <strong class="text-foreground">Não tem volta.</strong>
        </p>
        <p class="text-xs text-muted-foreground">
          Se for arquivar reversível, fecha esse modal e use o botão "Arquivar"
          dentro da edição.
        </p>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Digite{" "}
            <code class="rounded bg-accent px-1 py-0.5 text-[10px] font-mono">
              {bank.name}
            </code>{" "}
            pra confirmar
          </span>
          <input
            type="text"
            value={confirmText}
            onInput={(e) => setConfirmText((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-destructive"
          />
        </label>
        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => (deleting.value = null)}
            disabled={submitting}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={!ok || submitting}
            class="inline-flex items-center gap-1.5 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-1.5 text-xs font-bold text-red-600 transition-colors hover:bg-red-500/15 disabled:opacity-40 dark:text-red-400"
          >
            {submitting ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Trash2 width="1em" height="1em" />
            )}
            Excluir
          </button>
        </div>
      </div>
    </Modal>
  );
}

function FormSection({ title, children }: { title: string; children: any }) {
  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-3">
      <h3 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function VisibilityOption({
  icon: Icon,
  label,
  desc,
  active,
  onClick
}: {
  icon: typeof Globe;
  label: string;
  desc: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`flex items-start gap-2 rounded-md border p-3 text-left text-xs transition-colors ${
        active
          ? "border-primary/50 bg-primary/5"
          : "border-border bg-background hover:bg-accent/40"
      }`}
    >
      <Icon
        width="1.3em"
        height="1.3em"
        className={active ? "text-primary" : "text-muted-foreground"}
      />
      <div class="min-w-0 flex-1">
        <div class="font-bold">{label}</div>
        <div class="text-[10px] text-muted-foreground">{desc}</div>
      </div>
    </button>
  );
}

function OwnerPicker({
  selectedId,
  onChange,
  currentEmail
}: {
  selectedId: string;
  onChange: (id: string) => void;
  currentEmail?: string | null;
}) {
  const [q, setQ] = useState("");
  const [picking, setPicking] = useState(false);
  const users = allUsers.value;
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users.slice(0, 30);
    return users
      .filter((u) =>
        `${u.email} ${u.name} ${u.apelido ?? ""}`.toLowerCase().includes(term)
      )
      .slice(0, 30);
  }, [users, q]);
  const selUser = users.find((u) => u.id === selectedId);
  const displayEmail = selUser?.email ?? currentEmail ?? null;
  const displayName = selUser?.name ?? null;

  if (!picking) {
    return (
      <div class="flex items-center justify-between gap-2 rounded-md border border-border bg-background/50 px-3 py-2 text-sm">
        <div class="min-w-0 flex-1">
          {displayEmail ? (
            <>
              {displayName && <div class="truncate font-semibold">{displayName}</div>}
              <div class="truncate font-mono text-[11px] text-muted-foreground">
                {displayEmail}
              </div>
            </>
          ) : (
            <span class="text-xs italic text-muted-foreground">Sem dono definido</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setPicking(true)}
          class="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent"
        >
          Trocar
        </button>
      </div>
    );
  }

  return (
    <div class="space-y-2">
      <div class="relative">
        <Search
          width="1em"
          height="1em"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
          placeholder="Buscar usuário por email, nome…"
          autoFocus
          class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <div class="max-h-[240px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-xs italic text-muted-foreground">
            Nenhum usuário encontrado.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.map((u) => {
              const isSel = selectedId === u.id;
              return (
                <li
                  key={u.id}
                  class={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs transition-colors ${
                    isSel ? "bg-primary/10" : "hover:bg-accent/40"
                  }`}
                  onClick={() => {
                    onChange(u.id);
                    setPicking(false);
                  }}
                >
                  <input
                    type="radio"
                    checked={isSel}
                    readOnly
                    aria-label={`Selecionar ${u.email}`}
                    class="size-3.5"
                  />
                  <div class="min-w-0 flex-1">
                    <div class="truncate font-semibold">{u.name}</div>
                    <div class="truncate font-mono text-[10px] text-muted-foreground">
                      {u.email}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={() => setPicking(false)}
        class="text-[11px] text-muted-foreground hover:text-foreground underline"
      >
        Cancelar troca
      </button>
    </div>
  );
}

/** Multi-select de turmas (groups). Lista é renderizada in-line (não popover);
 *  usuário marca/desmarca várias antes de submeter o form de novo/editar banco.
 *  Submissão final do form é que dispara o patch nos groups. */
function BankGroupsMultiSelect({
  groups,
  selected,
  onChange
}: {
  groups: GroupListRow[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [q, setQ] = useState("");
  const selSet = new Set(selected);
  const filtered = q.trim()
    ? groups.filter((g) =>
        `${g.name} ${g.companyName ?? ""} ${g.description ?? ""}`
          .toLowerCase()
          .includes(q.trim().toLowerCase())
      )
    : [...groups].sort((a, b) => {
        const aSel = selSet.has(a.id) ? 0 : 1;
        const bSel = selSet.has(b.id) ? 0 : 1;
        if (aSel !== bSel) return aSel - bSel;
        return a.name.localeCompare(b.name);
      });

  function toggle(id: string) {
    if (selSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  }

  return (
    <div class="space-y-2">
      <div class="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          {selected.length} turma{selected.length !== 1 ? "s" : ""} selecionada
          {selected.length !== 1 ? "s" : ""}
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            class="underline hover:text-foreground"
          >
            Limpar
          </button>
        )}
      </div>
      <input
        type="search"
        value={q}
        onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        placeholder="Buscar turma…"
        class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs outline-none focus:border-primary"
      />
      <div class="max-h-[180px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-[11px] italic text-muted-foreground">
            Nenhuma turma encontrada.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.slice(0, 50).map((g) => {
              const checked = selSet.has(g.id);
              return (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => toggle(g.id)}
                    class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                      checked ? "bg-primary/10" : "hover:bg-accent/30"
                    }`}
                  >
                    <span
                      class={`inline-flex size-3.5 shrink-0 items-center justify-center rounded border ${
                        checked
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background"
                      }`}
                    >
                      {checked && <Check className="size-2.5" />}
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block truncate font-semibold">{g.name}</span>
                      <span class="block truncate text-[10px] text-muted-foreground">
                        {g.companyName ?? "Plataforma"} · {g.memberCount} membro
                        {g.memberCount !== 1 ? "s" : ""} · {g.bankCount} banco
                        {g.bankCount !== 1 ? "s" : ""}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Multi-select de Grupos de Estudo (study_groups, peer-led). Mesma UX do
 *  BankGroupsMultiSelect, mas a submissão chama studyGroupAddBank por grupo. */
function BankStudyGroupsMultiSelect({
  groups,
  selected,
  onChange
}: {
  groups: StudyGroupListRow[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [q, setQ] = useState("");
  const selSet = new Set(selected);
  const filtered = q.trim()
    ? groups.filter((g) =>
        `${g.name} ${g.description ?? ""} ${g.createdByName ?? ""}`
          .toLowerCase()
          .includes(q.trim().toLowerCase())
      )
    : [...groups].sort((a, b) => {
        const aSel = selSet.has(a.id) ? 0 : 1;
        const bSel = selSet.has(b.id) ? 0 : 1;
        if (aSel !== bSel) return aSel - bSel;
        return a.name.localeCompare(b.name);
      });

  function toggle(id: string) {
    if (selSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  }

  return (
    <div class="space-y-2">
      <div class="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          {selected.length} grupo{selected.length !== 1 ? "s" : ""} selecionado
          {selected.length !== 1 ? "s" : ""}
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            class="underline hover:text-foreground"
          >
            Limpar
          </button>
        )}
      </div>
      <input
        type="search"
        value={q}
        onInput={(e) => setQ((e.target as HTMLInputElement).value)}
        placeholder="Buscar grupo de estudo…"
        class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs outline-none focus:border-primary"
      />
      <div class="max-h-[180px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-[11px] italic text-muted-foreground">
            Nenhum grupo de estudo encontrado.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.slice(0, 50).map((g) => {
              const checked = selSet.has(g.id);
              return (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => toggle(g.id)}
                    class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                      checked ? "bg-primary/10" : "hover:bg-accent/30"
                    }`}
                  >
                    <span
                      class={`inline-flex size-3.5 shrink-0 items-center justify-center rounded border ${
                        checked
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background"
                      }`}
                    >
                      {checked && <Check className="size-2.5" />}
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block truncate font-semibold">{g.name}</span>
                      <span class="block truncate text-[10px] text-muted-foreground">
                        {g.memberCount} membro{g.memberCount !== 1 ? "s" : ""} ·{" "}
                        {g.itemCount} item{g.itemCount !== 1 ? "s" : ""}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
